import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import { Switch } from '../buttons/Swtich';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { PortalReportsApi } from '../../api/portalReports';
import { Spinner } from '../Spinner';
import { MultiSelect } from '../selects/MultiSelect';

export function DuplicateViewModal({ isOpen, toggle, sourceView, currentClientId, onDuplicated }) {
    const { t } = useTranslation();
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [assignToPortal, setAssignToPortal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setSelectedIds([]);
        setAssignToPortal(false);
        setLoadingClients(true);
        new APIClient(API_URL + 'node-to-gsheet/sync-configuration/form-data')
            .call('GET')
            .then((data) => {
                if (data && Array.isArray(data.agency_clients)) {
                    setClients(data.agency_clients);
                }
                setLoadingClients(false);
            })
            .catch(() => setLoadingClients(false));
    }, [isOpen]);

    const eligibleClients = useMemo(() => {
        if (!currentClientId) return clients;
        return clients.filter((c) => String(c.id) !== String(currentClientId));
    }, [clients, currentClientId]);

    const handleDuplicate = () => {
        if (!sourceView) return;
        if (selectedIds.length === 0) {
            toast.error(t('portal_reports.duplicate_pick_at_least_one'));
            return;
        }
        setIsSaving(true);
        PortalReportsApi.duplicateView(sourceView.id, {
            target_client_ids: selectedIds.map((id) => parseInt(id, 10)),
            assign_to_portal: assignToPortal,
        }).then((response) => {
            setIsSaving(false);
            if (!response || response.status === 'ERROR') {
                toast.error((response && response.error_message) || t('portal_reports.duplicate_error'));
                return;
            }
            toast.success(t('portal_reports.duplicate_success', { count: selectedIds.length }));
            if (onDuplicated) onDuplicated(response.views || []);
        }).catch(() => {
            setIsSaving(false);
            toast.error(t('portal_reports.duplicate_error'));
        });
    };

    if (!sourceView) return null;

    return (
        <Modal isOpen={isOpen} toggle={isSaving ? undefined : toggle} size="md">
            <ModalHeader toggle={isSaving ? undefined : toggle}>
                {t('portal_reports.duplicate_title')}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="portal-reports-duplicate-source">
                        {t('portal_reports.duplicate_source_label')} <strong>{sourceView.name}</strong>
                    </p>

                    {loadingClients ? (
                        <div className="box-loader"><Spinner color="positive" /></div>
                    ) : eligibleClients.length === 0 ? (
                        <div className="portal-reports-duplicate-empty">
                            {t('portal_reports.duplicate_no_clients')}
                        </div>
                    ) : (
                        <div className="form-row portal-reports-duplicate-multiselect">
                            <MultiSelect
                                key={sourceView.id}
                                items={eligibleClients}
                                selectedItemIds={selectedIds}
                                onChange={setSelectedIds}
                                itemDataMap={(c) => ({
                                    name: c.name || t('portal_reports.duplicate_unnamed_client'),
                                    id: String(c.id),
                                    isActive: c.is_active,
                                })}
                                showOnlyActiveCheck={false}
                            />
                        </div>
                    )}

                    <div className="form-row saved-view-toggle-row">
                        <div className="saved-view-toggle-text">
                            <div className="saved-view-toggle-label">
                                {t('portal_reports.duplicate_assign_label')}
                            </div>
                            <div className="saved-view-toggle-hint">
                                {t('portal_reports.duplicate_assign_hint')}
                            </div>
                        </div>
                        <Switch
                            name="duplicateAssignToPortal"
                            checked={assignToPortal}
                            onChange={(e) => setAssignToPortal(e.target.checked)}
                            style={{ margin: 0 }}
                            disabled={isSaving}
                        />
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle} disabled={isSaving}>
                    {t('common.cancel')}
                </Button>
                <button
                    className="btn -alt"
                    onClick={handleDuplicate}
                    disabled={isSaving || selectedIds.length === 0}
                >
                    {isSaving
                        ? t('common.saving')
                        : t('portal_reports.duplicate_cta', { count: selectedIds.length })}
                </button>
            </ModalFooter>
        </Modal>
    );
}
